<template>
  <header class="app-header">
    <div class="app-header__inner page-container">
      <!-- Logo + 品牌 -->
      <router-link to="/" class="brand">
        <img class="brand__logo" src="/images/logo.png" alt="AI配音小狐狸" />
        <span class="brand__name">AI配音小狐狸</span>
      </router-link>

      <!-- 主导航 -->
      <nav class="nav">
        <router-link
          v-for="item in navs"
          :key="item.path"
          :to="item.path"
          class="nav__item"
          :class="{ 'nav__item--on': isActive(item) }"
        >{{ item.name }}</router-link>
      </nav>

      <!-- 右侧用户区 -->
      <div class="user">
        <!-- 打开小程序（URL Scheme 唤起，开发模式模拟） -->
        <button class="open-mini" @click="onOpenMini">
          <svg class="open-mini__icon" viewBox="0 0 24 24" fill="currentColor" width="18" height="18" aria-hidden="true">
            <path d="M8.7 3.5C4.9 3.5 2 6.2 2 9.4c0 1.9 1.1 3.5 2.8 4.6-.2.8-.7 1.8-.9 2.2-.1.2 0 .4.2.5.2 0 .6-.1 1.2-.4 1-.6 1.6-1 2-1.3.7.2 1.4.3 2.1.3h.3c-.1-.5-.2-1-.2-1.5 0-3 2.8-5.4 6.2-5.4h.3C15.5 5 12.4 3.5 8.7 3.5zm-2.9 4c.5 0 .9.4.9.9s-.4.9-.9.9-.9-.4-.9-.9.4-.9.9-.9zm5.8 0c.5 0 .9.4.9.9s-.4.9-.9.9-.9-.4-.9-.9.4-.9.9-.9z" />
            <path d="M22 14.1c0-2.6-2.5-4.7-5.5-4.7s-5.5 2.1-5.5 4.7 2.5 4.7 5.5 4.7c.6 0 1.2-.1 1.7-.3.4.3.9.6 1.7 1 .3.1.7.1.9-.1.1-.2 0-.4-.2-.7-.2-.4-.6-1.1-.8-1.7 1.4-.8 2.2-2.1 2.2-3.4zm-8.3-.7c-.4 0-.7-.3-.7-.7s.3-.7.7-.7.7.3.7.7-.3.7-.7.7zm5.5 0c-.4 0-.7-.3-.7-.7s.3-.7.7-.7.7.3.7.7-.3.7-.7.7z" />
          </svg>
          <span>打开小程序</span>
        </button>

        <template v-if="userStore.isLoggedIn">
          <div class="user__menu" @mouseenter="menuOpen = true" @mouseleave="menuOpen = false">
            <div class="user__info">
              <img class="user__avatar" :src="avatar" alt="头像" />
              <span class="user__name">{{ userStore.nickname }}</span>
              <span class="user__arrow">▾</span>
            </div>
            <transition name="drop">
              <div v-if="menuOpen" class="user__dropdown card">
                <router-link to="/profile" class="user__menu-item" @click="menuOpen = false">我的主页</router-link>
                <router-link to="/settings" class="user__menu-item" @click="menuOpen = false">设置</router-link>
                <div class="user__menu-divider"></div>
                <div class="user__menu-item user__menu-item--danger" @click="onLogout">退出登录</div>
              </div>
            </transition>
          </div>
        </template>
        <template v-else>
          <router-link to="/login" class="btn btn--primary">微信扫码登录</router-link>
        </template>
      </div>
    </div>
  </header>
</template>

<script setup>
import { ref, computed } from 'vue'
import { useRoute, useRouter } from 'vue-router'
import { useUserStore } from '@/store/user'
import { toast } from '@/utils/toast'
import { SITE_CONFIG } from '@/config'

const route = useRoute()
const router = useRouter()
const userStore = useUserStore()
const menuOpen = ref(false)

const navs = [
  { path: '/', name: '配音' },
  { path: '/voices', name: '音色库' },
  { path: '/works', name: '作品' }
]

const avatar = computed(
  () => userStore.avatarUrl || 'https://arya-asset.oss-cn-shanghai.aliyuncs.com/echo/common/logo4.png'
)

function isActive(item) {
  if (item.path === '/') return route.path === '/'
  return route.path.startsWith(item.path)
}

function onLogout() {
  userStore.logout()
  toast.success('已退出登录')
  router.push('/login')
}

/* ============ 打开小程序（URL Scheme 唤起，开发模式模拟） ============ */
function onOpenMini() {
  tryScheme()
  toast.info('正在唤起微信小程序…')
}

// 尝试用 URL Scheme 唤起微信小程序（PC 端通常无响应，属于开发模拟）
function tryScheme() {
  const scheme = SITE_CONFIG.miniProgramScheme
  if (!scheme) return
  const a = document.createElement('a')
  a.href = scheme
  a.style.display = 'none'
  document.body.appendChild(a)
  a.click()
  a.remove()
}
</script>

<style scoped>
.app-header {
  position: sticky;
  top: 0;
  z-index: 100;
  height: var(--header-h);
  background: rgba(255, 253, 248, 0.92);
  backdrop-filter: blur(10px);
  border-bottom: 1px solid var(--color-border);
}

.app-header__inner {
  display: flex;
  align-items: center;
  gap: 32px;
  height: 100%;
}

.brand {
  display: flex;
  align-items: center;
  gap: 10px;
  flex-shrink: 0;
}

.brand__logo {
  width: 34px;
  height: 34px;
  object-fit: contain;
}

.brand__name {
  font-size: 18px;
  font-weight: 700;
  color: var(--color-text);
  letter-spacing: 0.5px;
  white-space: nowrap;
}

.nav {
  display: flex;
  align-items: center;
  gap: 6px;
  flex: 1;
}

.nav__item {
  padding: 8px 16px;
  font-size: 15px;
  color: var(--color-text-2);
  border-radius: var(--radius-md);
  transition: all 0.15s ease;
}

.nav__item:hover {
  color: var(--color-primary);
  background: var(--color-bg-hover);
}

.nav__item--on {
  color: var(--color-primary);
  font-weight: 600;
}

/* 右侧用户区（flex 布局，水平排列） */
.user {
  display: flex;
  align-items: center;
  gap: 14px;
  flex-shrink: 0;
}

/* 打开小程序按钮：胶囊形 + 图标（参考文抓抓站内设计） */
.open-mini {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  height: 38px;
  padding: 0 16px;
  border: 1px solid var(--color-border-2);
  border-radius: 999px;
  background: var(--color-bg-card);
  font-family: inherit;
  font-size: 13px;
  font-weight: 600;
  color: var(--color-text);
  cursor: pointer;
  transition: all 0.15s ease;
  white-space: nowrap;
}

.open-mini:hover {
  border-color: var(--color-primary);
  color: var(--color-primary);
  background: var(--color-primary-light);
}

.open-mini__icon {
  flex-shrink: 0;
}

.user__menu {
  position: relative;
}

.user__info {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 6px 10px;
  border-radius: var(--radius-md);
  cursor: pointer;
  transition: background 0.15s ease;
}

.user__info:hover {
  background: var(--color-bg-hover);
}

.user__avatar {
  width: 32px;
  height: 32px;
  border-radius: 50%;
  object-fit: cover;
  border: 1px solid var(--color-border);
}

.user__name {
  max-width: 120px;
  font-size: 14px;
  color: var(--color-text);
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.user__arrow {
  font-size: 12px;
  color: var(--color-text-3);
}

.user__dropdown {
  position: absolute;
  top: calc(100% + 8px);
  right: 0;
  width: 160px;
  padding: 6px;
  box-shadow: var(--shadow-lg);
}

.user__menu-item {
  display: block;
  padding: 9px 14px;
  font-size: 14px;
  color: var(--color-text-2);
  border-radius: var(--radius-sm);
  cursor: pointer;
  transition: all 0.15s ease;
}

.user__menu-item:hover {
  background: var(--color-bg-hover);
  color: var(--color-primary);
}

.user__menu-item--danger {
  color: var(--color-danger);
}

.user__menu-item--danger:hover {
  background: var(--color-danger-bg);
  color: var(--color-danger);
}

.user__menu-divider {
  height: 1px;
  background: var(--color-border);
  margin: 4px 0;
}

.drop-enter-active,
.drop-leave-active {
  transition: all 0.15s ease;
}

.drop-enter-from,
.drop-leave-to {
  opacity: 0;
  transform: translateY(-6px);
}
</style>
